import { useEffect, useState } from "react";
import { getSeoOverride, subscribeSeo } from "../lib/seo";

// Uses React 19 metadata hoisting for title/meta/link and injects JSON-LD.
export default function Seo({ title, description, canonical, image, jsonLd, noindex }) {
  const [, force] = useState(0);
  useEffect(() => subscribeSeo(() => force((n) => n + 1)), []);
  useEffect(() => { document.querySelectorAll("head meta[data-static], head title[data-static]").forEach((m) => m.remove()); }, []);
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const ov = getSeoOverride()[path] || {};
  title = ov.title || title;
  description = ov.description || description;

  useEffect(() => {
    if (!jsonLd) return;
    const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    const nodes = blocks.map((b) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo", "1");
      s.text = JSON.stringify(b);
      document.head.appendChild(s);
      return s;
    });
    return () => nodes.forEach((n) => n.remove());
  }, [jsonLd]);

  const url = canonical || (typeof window !== "undefined" ? `https://reactiontests.net${window.location.pathname.replace(/\/+$/, "") || "/"}` : "");
  const img = image || "https://reactiontests.net/og.png";

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, follow" />}
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={img} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </>
  );
}

export function faqJsonLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(([q, a]) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

export function breadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export function videoGameJsonLd({ name, url, description, genre = "Reflex Game", multiplayer = false, plays = 0 }) {
  const j = {
    "@context": "https://schema.org",
    "@type": "VideoGame",
    name,
    url,
    description,
    genre,
    applicationCategory: "GameApplication",
    gamePlatform: "Web browser",
    operatingSystem: "Any (browser-based)",
    playMode: multiplayer ? "MultiPlayer" : "SinglePlayer",
    image: "https://reactiontests.net/og-image.jpg",
    inLanguage: "en",
    isAccessibleForFree: true,
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    publisher: { "@type": "Organization", name: "ReactionLab", url: "https://reactiontests.net/" },
  };
  if (plays > 0) {
    j.interactionStatistic = {
      "@type": "InteractionCounter",
      interactionType: "https://schema.org/PlayAction",
      userInteractionCount: plays,
    };
  }
  return j;
}

export function datasetJsonLd({ name, description, url, sampleSize }) {
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name,
    description,
    url,
    creator: { "@type": "Organization", name: "ReactionLab", url: "https://reactiontests.net/" },
    isAccessibleForFree: true,
    ...(sampleSize ? { variableMeasured: "Reaction time (ms)", measurementTechnique: "Browser-based reaction time test", numberOfItems: sampleSize } : {}),
  };
}

export function webAppJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "@id": "https://reactiontests.net/#app",
    name: "ReactionLab",
    url: "https://reactiontests.net/",
    applicationCategory: "GameApplication",
    operatingSystem: "Any (browser-based)",
    browserRequirements: "Requires JavaScript. Works in any modern browser.",
    image: "https://reactiontests.net/og-image.jpg",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    publisher: { "@type": "Organization", name: "ReactionLab", url: "https://reactiontests.net/" },
    featureList: [
      "Classic, audio and go/no-go reaction time tests",
      "Choice and target reaction tests",
      "15 original reflex games with global leaderboards",
      "7 brain games with a combined Brain Score",
      "Percentile ranking and personal bests",
      "Daily challenges, streaks and weekly tournaments",
      "Personal dashboard with an AI reaction coach",
      "Shareable result cards and friend challenges",
    ],
    description: "Free online reaction time test and reaction-performance platform. Measure your reaction speed in milliseconds, see your percentile, compete on leaderboards, and challenge friends. Not a medical or IQ assessment.",
  };
}
